import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import * as JourneySelectors from '@/selectors/journeySelectors';
import { JourneyThemeModel } from '@/typings/api/emailCenter';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'umi';
import { useJourneyFromSidebar } from './useEmailCenterJourney';

/**
 * Theme of the Email Center's selected journey (falling back to the first
 * journey), fetched once per journey. Preview panes use it so templates render
 * with the journey's real branding even before the Theme tab was visited.
 */
export function useJourneyTheme(): {
  journeyHashId: string | null;
  theme: JourneyThemeModel | null;
  themeLoaded: boolean;
} {
  const dispatch = useDispatch();
  const defaultJourney = useSelector(JourneySelectors.selectDefaultLeadSource);
  const selectedHashId = useSelector(
    EmailCenterSelectors.selectSelectedJourneyHashId,
  );
  const themeState = useSelector(EmailCenterSelectors.selectJourneyThemeState);
  const sidebarJourney = useSelector(JourneySelectors.selectActiveJourney);
  const journeyFromSidebar = useJourneyFromSidebar();

  // In lead generation the sidebar owns the journey, and this hook runs on
  // surfaces that never mount `useEmailCenterJourney` to mirror it into the
  // store (a template preview, a fragment editor) — so it resolves the sidebar
  // journey itself instead of trusting a selection left over from another page.
  const journeyHashId = journeyFromSidebar
    ? sidebarJourney?.hashId ?? selectedHashId ?? defaultJourney?.hashId ?? null
    : selectedHashId ?? defaultJourney?.hashId ?? null;
  const isCurrent = themeState.journeyHashId === journeyHashId;

  useEffect(() => {
    if (!journeyHashId) return;
    if (isCurrent && themeState.loaded) return;
    dispatch({
      type: EMAIL_CENTER_ACTIONS.FETCH_JOURNEY_THEME,
      payload: { journeyHashId },
    });
  }, [journeyHashId, isCurrent, themeState.loaded, dispatch]);

  return useMemo(
    () => ({
      journeyHashId,
      theme: isCurrent ? themeState.theme : null,
      themeLoaded: isCurrent && themeState.loaded,
    }),
    [journeyHashId, isCurrent, themeState.theme, themeState.loaded],
  );
}
